import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import { useKpiChartData } from './hooks/useKpiChartData';
import KpiChart from './KpiChart';

const CompanyKpiDetailModal = ({ 
  open, 
  onClose, 
  compCd, 
  companyName, 
  site,
  colorMap,
}) => {
  const { data, loading, error, fetchKpiChartData } = useKpiChartData();
  const [selectedRange, setSelectedRange] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (open && compCd) {
      fetchKpiChartData(site, compCd, selectedDate, selectedRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, compCd, site, selectedDate, selectedRange]);

  const handleRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setSelectedRange(newRange);
    }
  };

  const handleDateChange = (event) => {
    setSelectedDate(new Date(event.target.value));
  };

  const getRangeLabel = (range) => {
    switch (range) {
      case 'day': return '일별';
      case 'week': return '주별';
      case 'month': return '월별';
      default: return '주별';
    }
  };

  const getChartTypeByIndicator = (kpiIndicatorCd) => {
    if (kpiIndicatorCd?.includes('RATE') || kpiIndicatorCd?.includes('RATIO')) {
      return 'area';
    } else if (kpiIndicatorCd?.includes('COUNT') || kpiIndicatorCd?.includes('QUANTITY')) {
      return 'bar';
    }
    return 'line';
  };

  const getColorsByCategory = (categoryCd) => {
    return colorMap[categoryCd] || ['#1976d2', '#ff9800'];
  };

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: { xs: '95vh', sm: '90vh', md: '85vh' },
          maxHeight: { xs: '95vh', sm: '90vh', md: '85vh' },
          borderRadius: 2,
          overflow: 'hidden',
          m: { xs: 1, sm: 2, md: 3 }
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          m: 0, 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 0,
            flex: 1
          }}>
            <BusinessIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 24 } }} />
            <Typography 
              variant="h6" 
              component="div" 
              fontWeight={600}
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0
              }}
            >
              {companyName} KPI 상세 분석
            </Typography>
            <Chip 
              label={`${getRangeLabel(selectedRange)} 데이터`}
              size="small"
              sx={{ 
                backgroundColor: '#e3f2fd', 
                color: '#1976d2',
                fontWeight: 'bold',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: '24px', sm: '28px' }
              }}
            />
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ 
              color: (theme) => theme.palette.grey[500],
              alignSelf: { xs: 'flex-end', sm: 'center' },
              mt: { xs: -0.5, sm: 0 }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: { xs: 1, sm: 2 }, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          flex: 1
        }}
      >
        {/* 기간 선택 컨트롤 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 1, sm: 2 }, 
          mb: 2,
          p: { xs: 1, sm: 1.5 },
          backgroundColor: '#fafafa',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          flexShrink: 0,
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap'
        }}>
          <ToggleButtonGroup
            value={selectedRange}
            exclusive
            onChange={handleRangeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: { xs: 1, sm: 1.5 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minWidth: { xs: '50px', sm: 'auto' }
              }
            }}
          >
            <ToggleButton value="day">일별</ToggleButton>
            <ToggleButton value="week">주별</ToggleButton>
            <ToggleButton value="month">월별</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}>
            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={handleDateChange}
              style={{
                padding: '6px 10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '13px',
                minWidth: '140px'
              }}
            />

            <Typography 
              variant="body2" 
              color="textSecondary"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                whiteSpace: { xs: 'normal', sm: 'nowrap' }
              }}
            >
              기준일: {selectedDate.toLocaleDateString('ko-KR')}
            </Typography>
          </Box>
        </Box>

        {/* 로딩 상태 */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            flex: 1,
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="textSecondary">
              KPI 차트 데이터를 불러오는 중...
            </Typography>
          </Box>
        )}

        {/* 에러 상태 */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        {/* 차트 데이터 - 위아래 배치, 스크롤 없이 모두 보이게 */}
        {!loading && !error && data && data.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1.5, 
            flex: 1, 
            minHeight: 0 
          }}>
            {data.map((kpiData, index) => (
              <Box 
                key={kpiData.kpiIndicatorCd || index}
                sx={{ 
                  p: { xs: 1, sm: 1.5 }, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2,
                  backgroundColor: 'white',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  minHeight: 0
                }}
              >
                {/* KPI 제목 및 정보 - 최대한 컴팩트하게 */}
                <Box sx={{ mb: { xs: 0.5, sm: 0.8 }, flexShrink: 0 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ 
                    color: '#1976d2',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    lineHeight: 1.1,
                    mb: { xs: 0.3, sm: 0.5 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {kpiData.kpiTitle || kpiData.kpiIndicatorCd}
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: { xs: 0.3, sm: 0.5 }, 
                    flexWrap: 'wrap' 
                  }}>
                    <Chip 
                      label={kpiData.categoryNm || '기본'}
                      size="small"
                      sx={{ 
                        backgroundColor: getColorsByCategory(kpiData.categoryCd)[0],
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        height: { xs: '20px', sm: '22px' }
                      }}
                    />
                    
                    {kpiData.targetType && (
                      <Chip 
                        label={`목표: ${kpiData.targetValue}${kpiData.unit || ''}`}
                        size="small"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          height: { xs: '20px', sm: '22px' },
                          backgroundColor: '#ff5722',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* 차트 - 컨테이너에 맞춰 완전 반응형 */}
                <Box sx={{ 
                  flexGrow: 1, 
                  minHeight: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <KpiChart
                    data={kpiData.chartData}
                    title=""
                    unit={kpiData.unit}
                    chartType={kpiData.chartType || getChartTypeByIndicator(kpiData.kpiIndicatorCd)}
                    height="100%"
                    colors={[getColorsByCategory(kpiData.categoryCd)[0], '#ff5722']}
                    targetValue={kpiData.targetValue}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* 데이터가 없을 때 */}
        {!loading && !error && (!data || data.length === 0) && (
          <Box sx={{ 
            textAlign: 'center', 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px dashed #ccc'
          }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              표시할 KPI 데이터가 없습니다
            </Typography>
            <Typography variant="body2" color="textSecondary">
              선택한 기간에 해당하는 KPI 데이터를 찾을 수 없습니다.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5', flexShrink: 0 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyKpiDetailModal;